import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import type { MessageDescriptor } from 'react-intl';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { faCreditCard } from '@fortawesome/pro-light-svg-icons/faCreditCard';
import { SMLRouteParts, SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { SMLNavItem } from '../../navigation/smlNavigation';
import { smlNavigationChromeMessages, smlNavigationMessages } from '../../navigation/smlNavigationMessages';
import { SMLSectionTab } from '../SMLSectionTabs/SMLSectionTabs';
import { insightsTabs } from '../../insights/insightsTabs';
import { postsTabs } from '../../promote/posts/postsTabs';
import { pitchTabs } from '../../outreach/pitchTabs';
import { chartsTabs } from '../../charts/chartsTabs';
import { SMLSettingsTabDef, SMLSettingsTabId } from '../../../settings/tabs/types';
import { settingsTabTarget } from '../../../settings/tabs/settingsTabTarget';
import { accountMenuMessages } from '../../navigation/account/accountMenuMessages';

/** One row of "Pages and actions" in the command palette (design navigation-v2 e 5d). */
export interface PalettePage {
  id: string;
  /** The page name, or the item and the tab: "Charts · Artists". */
  title: MessageDescriptor;
  /** For a tab: the item name that `title` puts in front. */
  parent?: MessageDescriptor;
  kind: 'page' | 'settings';
  icon: IconDefinition;
  route: SMLRouteParts;
  params?: Record<string, string>;
  /** Other names the row is found by: old page names ("Channels", "Browse", "Calendar"). */
  aliases: MessageDescriptor[];
  /** A menu item: listed while the search text is empty. */
  inMenu: boolean;
}

/** The menu items whose page has tabs, and the tabs (navigation v2). */
const TABS_OF_ITEM: Record<string, () => SMLSectionTab[]> = {
  insights: () => insightsTabs(undefined),
  promotePosts: () => postsTabs(undefined, undefined),
  outreachPitch: () => pitchTabs(undefined),
  charts: () => chartsTabs(undefined),
};

/** The old names of the Settings tabs. Channels moved into Settings as Connections. */
const SETTINGS_ALIASES: Partial<Record<SMLSettingsTabId, MessageDescriptor[]>> = {
  [SMLSettingsTabId.connections]: [smlNavigationMessages.promoteChannels],
};

/**
 * Every page the palette goes to: the menu items, each tab of a page with tabs (only of a visible
 * item), the Search page, the Plans page and every
 * Settings tab.
 */
export function palettePages(items: SMLNavItem[], settingsTabs: SMLSettingsTabDef[]): PalettePage[] {
  const pages: PalettePage[] = [];
  for (const item of items) {
    pages.push({
      id: item.id,
      title: item.label,
      kind: 'page',
      icon: item.icon,
      route: item.route,
      aliases: item.aliases ?? [],
      inMenu: true,
    });
  }
  for (const item of items) {
    const tabs = TABS_OF_ITEM[item.id]?.() ?? [];
    for (const tab of tabs) {
      pages.push({
        id: `${item.id}.${tab.id}`,
        title: tab.label,
        parent: item.label,
        kind: 'page',
        icon: item.icon,
        route: tab.route,
        params: tab.query,
        aliases: [],
        inMenu: false,
      });
    }
  }
  // Search (the old Browse) is not a menu item; the palette still finds it by both names.
  pages.push({
    id: 'search',
    title: smlNavigationChromeMessages.search,
    kind: 'page',
    icon: faMagnifyingGlass,
    route: SMLRoutes.browse,
    aliases: [smlNavigationMessages.browse],
    inMenu: false,
  });
  // The plans page is not a menu item (the account menu opens billing); the palette finds it.
  pages.push({
    id: 'plans',
    title: smlNavigationChromeMessages.plans,
    kind: 'page',
    icon: faCreditCard,
    route: SMLRoutes.plans,
    aliases: [accountMenuMessages.plansAndBilling],
    inMenu: false,
  });
  for (const tab of settingsTabs) {
    const target = settingsTabTarget(tab.id);
    pages.push({
      id: `settings.${tab.id}`,
      title: tab.label,
      kind: 'settings',
      icon: tab.icon,
      route: target.route,
      params: target.params,
      aliases: SETTINGS_ALIASES[tab.id] ?? [],
      inMenu: false,
    });
  }
  return pages;
}
